import { Fragment } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useBusinessUnits } from './api/useBusinessUnits';
import Login from './components/Auth/Login';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import ActiveUser from './components/Auth/ActiveUser';
import Automations from './components/Automations/Automations';
import Journeys from './components/Journeys/Journeys';
import JourneyDetail from './components/Journeys/JourneyDetail';
import DataExtensions from './components/DataExtensions/DataExtensions';
import EmailStudio from './components/EmailStudio/EmailStudio';
import CloudPages from './components/CloudPages/CloudPages';
import AuditHippoUsers from './components/AuditHippoUsers/AuditHippoUsers';
import BuComparison from './components/BuComparison/BuComparison';
import AutomationHealth from './components/Monitoring/AutomationHealth';
import JourneyHealth from './components/Monitoring/JourneyHealth';
import SubscriberHealth from './components/Monitoring/SubscriberHealth';
import AdminLogin from './components/Admin/AdminLogin';
import MCConnections from './components/Admin/MCConnections';
import UserManagement from './components/Admin/UserManagement';
import BillingPlan from './components/Admin/BillingPlan';

function hasUserSession() {
  return localStorage.getItem('isLoggedIn') === 'true'
    && !!localStorage.getItem('userAuthToken')
    && !!localStorage.getItem('userSessionId');
}

function hasAdminSession() {
  return localStorage.getItem('isAdminLoggedIn') === 'true'
    && !!localStorage.getItem('adminAuthToken')
    && !!localStorage.getItem('adminSessionId');
}

function PrivateRoute({ children }) {
  return hasUserSession()
    ? children
    : <Navigate to="/login" replace />;
}

function AdminPrivateRoute({ children }) {
  return hasAdminSession()
    ? children
    : <Navigate to="/admin/login" replace />;
}

// Data pages fetch on mount, so switching the working BU has to remount them:
// every request then re-runs under the new mid and page state (pagination,
// filters, expanded rows, open detail panels) resets with it.
function BuScoped({ children }) {
  const { selectedBu } = useBusinessUnits();
  return <Fragment key={selectedBu ? selectedBu.id : 'default'}>{children}</Fragment>;
}

function PublicRoute({ children }) {
  return hasUserSession()
    ? <Navigate to="/automations" replace />
    : children;
}

const BASENAME = process.env.REACT_APP_BASENAME || process.env.PUBLIC_URL || '';

function App() {
  return (
    <BrowserRouter basename={BASENAME}>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/activeuser" element={<ActiveUser />} />
        <Route path="/automations" element={<PrivateRoute><BuScoped><Automations /></BuScoped></PrivateRoute>} />
        {/* Future pages */}
        <Route path="/journeys" element={<PrivateRoute><BuScoped><Journeys /></BuScoped></PrivateRoute>} />
        <Route path="/journeys/:id" element={<PrivateRoute><BuScoped><JourneyDetail /></BuScoped></PrivateRoute>} />
        <Route path="/data-extensions" element={<PrivateRoute><BuScoped><DataExtensions /></BuScoped></PrivateRoute>} />
        <Route path="/email-studio" element={<PrivateRoute><BuScoped><EmailStudio /></BuScoped></PrivateRoute>} />
        <Route path="/cloud-pages" element={<PrivateRoute><BuScoped><CloudPages /></BuScoped></PrivateRoute>} />
        <Route path="/audithippo-users" element={<PrivateRoute><BuScoped><AuditHippoUsers /></BuScoped></PrivateRoute>} />
        <Route path="/automation-health" element={<PrivateRoute><AutomationHealth /></PrivateRoute>} />
        <Route path="/journey-health" element={<PrivateRoute><JourneyHealth /></PrivateRoute>} />
        <Route path="/subscriber-health" element={<PrivateRoute><SubscriberHealth /></PrivateRoute>} />
        <Route path="/bu-comparison" element={<PrivateRoute><BuComparison /></PrivateRoute>} />
        <Route path="/admin" element={<Navigate to={localStorage.getItem('isAdminLoggedIn') === 'true' ? '/admin/mc-connections' : '/admin/login'} replace />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/mc-connections" element={<AdminPrivateRoute><MCConnections /></AdminPrivateRoute>} />
        <Route path="/admin/user-management" element={<AdminPrivateRoute><UserManagement /></AdminPrivateRoute>} />
        <Route path="/admin/billing" element={<AdminPrivateRoute><BillingPlan /></AdminPrivateRoute>} />
        {/* Catch-all: redirect unknown routes to login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
